(ns app.front.view.inspections.booking-dialog
  (:require [app.domain.blackout.model :as blackout-model]
            [app.domain.booking.model :as booking-model]
            [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.user.authority :as authority]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.inspections.booking-utils :as butil]
            [app.front.view.inspections.reassign-inspector :as inspection-reassign]
            [app.front.view.inspections.subs :as inspection-sub]
            [app.front.view.workspace.get :as ws-get]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


;;; Events

(def ^:private interceptors [(rf/inject-cofx :tr)])


;; Open the new booking dialog, ready to make a new booking
(rf/reg-event-fx ::new-booking
  (fn [{:keys [db]} [_ inspection]]
    (let [auth-group          (group-model/get-authority-group inspection)
          user                (:session/user db)
          can-assign?         (and (ff/enabled? :feature-flag.id/pre-issuance-inspections)
                                   (authority/authority-in-group? user auth-group))
          has-inspector-role? (group-model/any-required-authority-role? auth-group user #{:group.role/inspector})
          qualified-self?     (inspection-model/no-inspection-qualification-restriction?
                                {:user user :target inspection :inspection inspection})]
      (cond-> {:db (assoc db ::booking {:open?                                true
                                        :multiple-applicant-bookings-enabled? (:inspection/multiple-applicant-bookings? inspection)
                                        :inspection-id                        (:db/id inspection)
                                        :inspectors-loaded?                   (not can-assign?)
                                        :selected-inspector-id                (when (and can-assign? has-inspector-role? qualified-self?)
                                                                                (:db/id user))
                                        :date-time-slots                      [{}]})}
        can-assign? (assoc :comm/command {:command    :group/booking-inspectors
                                          :method     :get
                                          :data       {:authority-group-id (:db/id auth-group)}
                                          :on-success [::inspectors-fetched]})))))

(rf/reg-event-db ::inspectors-fetched
  (fn [db [_ _status inspectors]]
    (update db ::booking
            (fn [{:keys [selected-inspector-id] :as booking}]
              (assoc booking
                     :inspectors inspectors
                     :inspectors-loaded? true
                     :selected-inspector-id (when (contains? (du/entity-id-set inspectors) selected-inspector-id)
                                              selected-inspector-id))))))


;; Recurring inspection visits involve creating a new booking without the dialog
(rf/reg-event-db ::new-recurring-inspection-booking
  (fn [db [_ default-date]]
    (assoc db ::booking {:open?           false
                         :date-time-slots [{:date default-date}]})))


;; Open the edit booking dialog, ready to edit an existing booking
(rf/reg-event-fx ::edit-booking
  (fn [{:keys [db]} [_ booking]]
    (let [has-inspector-role? (group-model/any-required-authority-role?
                                (group-model/get-authority-group booking)
                                (:session/user db)
                                #{:group.role/inspector})]
      (cond-> {:db (-> db
                       (assoc ::booking (merge {:open?                true
                                                :inspection-id        (-> booking :entity/parent :db/id)
                                                :editing?             true
                                                :date-fields-visible? false
                                                :cancelling?          false
                                                :date-changeable?     (not= :booking.state/dispatched (:domain/state booking))
                                                :date-time-slots      [{}]}
                                               (select-keys booking
                                                            [:domain/state
                                                             :booking/requested-start-time
                                                             :booking/requested-end-time
                                                             :booking/actual-start-time
                                                             :booking/actual-end-time
                                                             :db/id
                                                             :booking/notes]))))}
        has-inspector-role? (assoc :dispatch [::inspection-reassign/start-reassign [booking]])))))


(rf/reg-event-fx ::dialog-close
  (fn [{:keys [db]} _]
    {:db       (dissoc db ::booking)
     :dispatch [::inspection-reassign/assign-done]}))


;; Create a new booking to backend
(rf/reg-event-fx ::create-booking
  (fn [{:keys [db]} [_ target-id]]
    (let [data         (get db ::booking)
          inspector-id (:selected-inspector-id data)]
      (butil/create-booking-command (merge {:target-id    target-id
                                            :on-success   [::booking-added inspector-id]
                                            :notes        (-> data :booking/notes)
                                            :inspector-id inspector-id}
                                           (select-keys data [:inspection-id :date-time-slots]))))))


;; Update an existing booking
(rf/reg-event-fx ::update-booking
  (fn [{:keys [db]} [_ target-id]]
    (let [{:keys      [date-time-slots]
           notes      :booking/notes
           booking-id :db/id}           (get db ::booking)
          payload                       (p/assoc-if {:booking-id booking-id}
                                                    :updated-time (-> date-time-slots butil/payload-time-data :dates first)
                                                    :notes notes)]
      {:comm/command {:command    :process/apply-signal
                      :data       {:process/signal [:process.signal/update-booking payload]
                                   :entity-id      target-id}
                      :on-success [::booking-updated booking-id]}})))


;; Cancel a booking
(rf/reg-event-fx ::cancel-booking
  (fn [{:keys [db]} [_ target-id]]
    (let [reason     (-> db ::booking :booking/cancel-reason)
          booking-id (-> db ::booking :db/id)
          payload    (p/assoc-if {:booking-id booking-id} :reason reason)]
      {:comm/command {:command    :process/apply-signal
                      :data       {:process/signal [:process.signal/cancel-booking payload]
                                   :entity-id      target-id}
                      :on-success [::booking-cancelled]}})))


(rf/reg-event-fx ::booking-cancelled
  interceptors
  (fn [{:keys [tr]} [_ status entities]]
    {:dispatch-n [[::entities/apply-changes status entities]
                  [::dialog-close]
                  [:notifications/add :toast :success :success (tr [:booking/cancelled])]]}))


(rf/reg-event-fx ::booking-updated
  interceptors
  (fn [{:keys [tr]} [_ _ status resp]]
    {:dispatch-n [[::entities/apply-changes status resp]
                  [::dialog-close]
                  [:notifications/add :toast :success :success (tr [:booking/changes-saved])]]}))


(rf/reg-event-fx ::booking-added
  interceptors
  (fn [{:keys [tr]} [_ inspector-id status resp]]
    {:dispatch-n [[::entities/apply-changes status resp]
                  [::dialog-close]
                  [:notifications/add :toast :success :success
                   (tr [(if inspector-id :booking/booking-dispatch-success :booking/booking-success)])]]}))


(rf/reg-event-db ::set-booking-data
  (fn [db [_ key data]]
    (assoc-in db [::booking key] data)))


(rf/reg-event-db ::set-selected-inspector
  (fn [db [_ inspector-id]]
    (assoc-in db [::booking :selected-inspector-id] inspector-id)))


(rf/reg-event-db ::set-booking-date-time-slot-data
  (fn [db [_ idx key data]]
    (assoc-in db [::booking :date-time-slots idx key] data)))


(rf/reg-event-db ::add-booking-date-time-slot
  (fn [db [_]]
    (update-in db [::booking :date-time-slots] conj {})))


(rf/reg-event-db ::remove-booking-date-time-slot
  (fn [db [_ idx]]
    (update-in db [::booking :date-time-slots]
               #(into (subvec % 0 idx) (subvec % (inc idx))))))


;;; Subs

(rf/reg-sub ::booking-data
  :-> ::booking)

(rf/reg-sub ::booking-date-time-slots
  :<- [::booking-data]
  :-> :date-time-slots)


(rf/reg-sub ::booking-date-time-slot-count
  :<- [::booking-date-time-slots]
  count)


(rf/reg-sub ::booking-date-time-slot
  :<- [::booking-date-time-slots]
  (fn [data [_ idx]]
    (get data (or idx 0))))


(rf/reg-sub ::reserved-dates
  :<- [::booking-date-time-slots]
  (fn [date-time-slots [_ this-date]]
    (disj (->> date-time-slots
               (map :date)
               set)
          this-date)))


(rf/reg-sub ::cancelling?
  :<- [::booking-data]
  (fn [data _]
    (-> data :cancelling? boolean)))


(rf/reg-sub ::date-fields-visible?
  :<- [::booking-data]
  :-> (comp boolean :date-fields-visible?))


(rf/reg-sub ::can-save?
  (fn [[_ auth-group]]
    [(rf/subscribe [::booking-data])
     (rf/subscribe [::first-allowed-date auth-group (js/Date.)])])
  (fn [[booking-data min-date] _]
    (let [{:keys [date-time-slots editing? date-fields-visible? cancelling?]} booking-data
          time-slots-valid?                                                   (every? (fn [{:keys [date time-slot-id]}]
                                                                                        (and (not (str/blank? date))
                                                                                             (date-util/on-or-after? date (js/Date. min-date))
                                                                                             time-slot-id))
                                                                                      date-time-slots)]
      (if editing?
        (and (not cancelling?)
             (or (not date-fields-visible?)
                 time-slots-valid?))
        (and (:inspectors-loaded? booking-data)
             time-slots-valid?)))))


(rf/reg-sub ::time-slots-enabled?
  :<- [::booking-date-time-slots]
  (fn [data [_ idx]]
    (let [{:keys [date]} (get data idx)]
      (not (str/blank? date)))))


(rf/reg-sub ::request-time-slots
  (fn [[_ auth-group date]]
    (rf/subscribe [::inspection-sub/availability auth-group date]))
  (fn [availability _]
    (->> (or (:availability/customer-time-slots availability)
             (:availability/time-slots availability))
         (sort-by (juxt :time-slot/start-hour :time-slot/start-minute)))))


(rf/reg-sub ::dialog-open?
  :<- [::booking-data]
  (fn [data _]
    (get data :open?)))


(rf/reg-sub ::selected-inspector-id
  :<- [::booking-data]
  :-> :selected-inspector-id)


(rf/reg-sub ::fetched-inspectors
  :<- [::booking-data]
  :-> :inspectors)


(rf/reg-sub ::inspection-supports-multiple-applicant-bookings?
  :<- [::booking-data]
  :-> :multiple-applicant-bookings-enabled?)


(rf/reg-sub ::multiple-bookings-enabled?
  (fn [_]
    [(rf/subscribe [::ws-subs/user-has-permission? :permission.phase/authority-read])
     (rf/subscribe [::ws-subs/has-feature? :group.feature/multiple-inspection-bookings-authority])
     (rf/subscribe [::ws-subs/has-feature? :group.feature/multiple-inspection-bookings-applicant])
     (rf/subscribe [::inspection-supports-multiple-applicant-bookings?])])
  (fn [[authority-user? authority-enabled? applicant-enabled? inspection-supports?]]
    (or (and authority-user? authority-enabled?)
        (and applicant-enabled? inspection-supports?))))


(defn- add-working-days [blackouts workdays date days tz]
  (when date
    (cond
      (or (some (partial blackout-model/applies-to-date?
                         (date-util/date-at-time-in-tz date 0 0 tz)) blackouts)
          (not (workdays (date-util/js-weekday-to-std (.getDay date)))))
      (recur blackouts
             workdays
             (date-util/goog-add-days date 1)
             days
             tz)
      (>= 0 days)
      date
      :else
      (recur blackouts
             workdays
             (date-util/goog-add-days date 1)
             (dec days)
             tz))))

(rf/reg-sub ::first-allowed-date
  (fn [[_ auth-group date]]
    [(rf/subscribe [::date-util/date-fn])
     (rf/subscribe [::inspection-sub/availability auth-group date])])
  (fn [[format-date availability] [_ auth-group now]]
    (let [[default-h
           default-min] booking-model/default-freeze-time
          h             (or (:availability/freeze-time-hour availability)
                            default-h)
          m             (or (:availability/freeze-time-minute availability)
                            default-min)
          group-tz      (:group/tz auth-group)
          blackouts     (blackout-model/current-group-blackouts auth-group now)
          workdays      inspection-model/standard-workdays
          date          (date-util/goog-date-time-start-of-day now)
          ;; too high number would crash the browser
          day-count     (min (or (:availability/freeze-time-days availability) 0) booking-model/max-freeze-time-days)
          freeze-days   (cond-> day-count
                          ;; start freeze time calculation from next day if this is a
                          ;; normal working day but we have already passed the freeze
                          ;; time deadline for today
                          (and (date-util/past? group-tz [h m] now)
                               (workdays (date-util/js-weekday-to-std (.getDay date)))
                               (->> blackouts
                                    (some (partial blackout-model/applies-to-date?
                                                   (date-util/date-at-time-in-tz date 0 0 group-tz)))
                                    not))
                          inc)]
      (format-date
        (add-working-days blackouts
                          workdays
                          date
                          freeze-days
                          group-tz)))))

;;; Dialog UI

(def ^:private select-container
  :div.bx--col-xs-12.bx--col-xl-5.my-4)


(defn- book-terminology? []
  (and (<sub [::ff/enabled? :feature-flag.id/pre-issuance-inspections])
       (<sub [:session/authority-user?])))

(defn render-date-picker
  "Used for both creating and editing an inspection request.
   Creation supports multiple booking date + time slot entries, editing does not. Both however store
   the entry or entries in a collection."
  [{:keys [tr disabled? fixed? idx auth-group]
    :or   {idx 0 disabled? false fixed? false}}]
  (let [{:keys [date time-slot-id]} (<sub [::booking-date-time-slot idx])
        time-slots-enabled?         (<sub [::time-slots-enabled? idx])
        all-time-slots              (<sub [::request-time-slots auth-group (or date (date-util/now))])
        single-time-slot?           (->> all-time-slots
                                         (group-by :time-slot/weekday)
                                         (every? #(-> % second count (= 1))))
        time-slots-for-date         (when date
                                      (->> all-time-slots
                                           (filter #(= (:time-slot/weekday %) (.getDay date)))))
        blackouts                   (blackout-model/current-group-blackouts auth-group)
        group-tz                    (:group/tz auth-group)
        can-remove?                 (-> (<sub [::booking-date-time-slot-count]) (> 1))
        reserved-dates              (<sub [::reserved-dates date])]
    [:div.w-full
     [:div.bx--row
      (if (<sub [::ws-get/fetching-config? (:group/id auth-group)])
        [:<>
         [select-container (cc/select-skeleton)]
         [select-container (cc/select-skeleton)]]
        [select-container {:class "required-input"}
         [ui/date-picker {:id                 "booking-date"
                          :fixed?             fixed?
                          :callback           (fn [d]
                                                (>evt [::set-booking-date-time-slot-data idx :date d])
                                                (>evt [::set-booking-date-time-slot-data idx :time-slot-id
                                                       (when d
                                                         (-> all-time-slots
                                                             (p/find-first #(= (:time-slot/weekday %) (.getDay d)))
                                                             :db/id))]))
                          :label-text         (if (book-terminology?)
                                                (tr [:booking/select-date])
                                                (tr [:booking/requested-date]))
                          :value              date
                          :disabled?          disabled?
                          :min-date           (<sub [::first-allowed-date auth-group (js/Date.)])
                          :allow-date?        (fn [date]
                                                (and (-> reserved-dates (contains? date) not)
                                                     (butil/allow-date? group-tz all-time-slots blackouts date)))
                          :report-disallowed? true}]])
      (when (not single-time-slot?)
        [select-container {:class "required-input"}
         [ui-select/select {:id           "time-slot"
                            :class-name   "booking-time-slot"
                            :empty-text   (tr [:select-option])
                            :label-text   (if (book-terminology?)
                                            (tr [:booking/time-slot])
                                            (tr [:booking/requested-time-slot]))
                            :value        time-slot-id
                            :on-change    #(>evt [::set-booking-date-time-slot-data idx :time-slot-id %])
                            :val->str     #(-> (p/find-first time-slots-for-date {:db/id %})
                                               (butil/time-slot-string group-tz))
                            :values       (map :db/id time-slots-for-date)
                            :allow-empty? false
                            :disabled     (or disabled?
                                              (not time-slots-enabled?))}]])
      (when can-remove?
        [:div.flex.flex-col.items-end
         [cc/button {:kind       :ghost
                     :size       :small
                     :icon       i/trash
                     :class-name "float-right w-auto"
                     :on-click   #(>evt [::remove-booking-date-time-slot idx])}
          (tr [:booking/remove-date])]])]
     (when can-remove? [:hr.border.border-ui-gray-30])]))


(defn- render-recurring-inspection-note
  "A line that informs the user the inspection they're making a request for is recurring"
  [tr]
  (let [booking    (<sub [::booking-data])
        inspection (<sub [::entities/entity (:inspection-id booking)])]
    (when (:inspection/recurring? inspection)
      [:div (tr [:booking/recurring-inspection] [(:inspection/recurrence-interval inspection)])])))


(defn render-comment-field
  "A shared render for booking notes / cancellation reason"
  [tr booking]
  (let [cancelling?  (<sub [::cancelling?])
        assoc-val    (fn [m k] (p/assoc-if m :value (get booking k)))
        props-cancel {:id          "cancel-reason"
                      :label-text  (tr [:booking/cancel-reason])
                      :placeholder (tr [:booking/cancel-placeholder])
                      :value       (:booking/cancel-reason booking)
                      :on-change   (fn [e]
                                     (rf/dispatch-sync [::set-booking-data
                                                        :booking/cancel-reason
                                                        (-> e futil/tval)]))}
        props-notes  {:id          "booking-notes"
                      :label-text  (tr [:booking/notes])
                      :placeholder (tr [:booking/notes-placeholder])
                      :value       (:booking/notes booking)
                      :on-change   (fn [e]
                                     (rf/dispatch-sync [::set-booking-data
                                                        :booking/notes
                                                        (-> e futil/tval)]))}]
    (if cancelling?
      [ui/text-area (assoc-val props-cancel :booking/cancel-reason)]
      [ui/text-area (assoc-val props-notes :booking/notes)])))


(defn inspection-request-instructions [{:group/keys [inspection-request-instructions] :as group}]
  (when (seq inspection-request-instructions)
    [:div.my-4
     [ui-rt/editor-legacy {:initial-value inspection-request-instructions
                           :id            (str (:db/id group) "-inspection-request-info")
                           :scroll?       true}]]))


(defn- inspectors-self-first [inspectors me]
  (let [me-id (:db/id me)]
    (concat (filter #(= me-id (:db/id %)) inspectors)
            (remove #(= me-id (:db/id %)) inspectors))))


(defn- render-inspector-select [tr target]
  (let [inspection  (<sub [::entities/entity (:inspection-id (<sub [::booking-data]))])
        me          (<sub [:session/user])
        qualified   (->> (<sub [::fetched-inspectors])
                         (filter #(inspection-model/no-inspection-qualification-restriction?
                                    (util/mmap {:user %} target inspection)))
                         (#(inspectors-self-first % me)))
        selected-id (<sub [::selected-inspector-id])
        selected    (p/find-first qualified {:db/id selected-id})]
    [:div.my-4
     [ui-select/select
      {:id         "booking-inspector"
       :label-text (tr [:booking/assign-inspector])
       :empty-text (tr [:none])
       :value      selected
       :val->str   user-model/full-name
       :on-change  #(>evt [::set-selected-inspector (:db/id %)])
       :values     qualified}]
     (when-not selected-id
       [:p.pt-2 (tr [:booking/no-inspector-dispatch-notice])])]))


(defn- render-new-booking-form [tr target auth-group]
  (let [multiple-bookings-enabled? (<sub [::multiple-bookings-enabled?])
        show-inspector-select?     (and (<sub [::ff/enabled? :feature-flag.id/pre-issuance-inspections])
                                        (authority/authority-in-group? (<sub [:session/user]) auth-group))]
    [:div.w-full
     (for-frag [idx (range (<sub [::booking-date-time-slot-count]))]
       [render-date-picker (util/mmap {:fixed? true}
                                      tr idx auth-group)])
     (when multiple-bookings-enabled?
       [:div.flex
        (cc/button
          {:kind     :tertiary
           :size     :small
           :icon     i/plus
           :on-click #(>evt [::add-booking-date-time-slot])}
          (tr [:booking/add-date]))])
     [render-recurring-inspection-note tr]
     (when show-inspector-select?
       (if (:inspectors-loaded? (<sub [::booking-data]))
         [render-inspector-select tr target]
         [:div.my-4 (cc/select-skeleton)]))
     [:div.my-4
      [render-comment-field tr (<sub [::booking-data])]]
     [inspection-request-instructions auth-group]]))


(defn- render-time-slot [tr {state :domain/state :as inspection} auth-group]
  (let [{:booking/keys [requested-start-time
                        requested-end-time
                        actual-start-time
                        actual-end-time]}    inspection
        group-tz                             (:group/tz auth-group)]
    [:p.pb-2.font-bold
     (if (= state :booking.state/dispatched)
       (tr [:booking/confirmed-to-occur] [(date-util/interval-in-tz actual-start-time actual-end-time group-tz)])
       (tr [:booking/requested-between] [(date-util/interval-in-tz requested-start-time requested-end-time group-tz)]))]))


(defn- render-cancel-toggle
  "A button for canceling the booking with a checkbox for confirming it"
  [tr on-cancel]
  (let [cancelling? (<sub [::cancelling?])]
    [:div.bx--row
     [:div.bx--col-xs-6
      (cc/checkbox
        {:id         "cancel-booking-confirmation"
         :label-text (tr [:booking/confirm-booking-cancel])
         :checked    cancelling?
         :on-change  #(>evt [::set-booking-data :cancelling? (not cancelling?)])})]
     [:div.bx--col-xs-6
      (cc/button
        {:kind             :secondary
         :class-name       "bg-red-600"
         :disabled         (not cancelling?)
         :on-click         on-cancel
         :icon-position    :right
         :icon             i/trash
         :icon-description (tr [:booking/cancel-booking])}
        (tr [:booking/cancel-booking]))]]))

(defn- render-edit-booking-form
  [target tr booking date-changeable? auth-group on-cancel can-reassign?]
  (let [date-fields-visible?        (<sub [::date-fields-visible?])
        cancelling?                 (<sub [::cancelling?])
        authority-group-id          (:db/id auth-group)
        {:keys [date time-slot-id]} (get-in booking [:date-time-slots 0])]
    [:div.w-full
     [render-time-slot tr booking auth-group]
     (if date-changeable?
       (if date-fields-visible?
         [render-date-picker (util/mmap {:disabled? cancelling?
                                         :fixed?    true
                                         :idx       0}
                                        tr auth-group date time-slot-id)]
         (cc/button
           {:kind       :tertiary
            :disabled   cancelling?
            :class-name "mb-4"
            :on-click   #(>evt [::set-booking-data :date-fields-visible? true])}
           (tr [:booking/change-date])))
       [:p.p-0 (tr [:booking/date-change-not-possible])])
     [render-recurring-inspection-note tr]
     [:div.my-4
      [render-comment-field tr booking]]
     [inspection-request-instructions auth-group]
     (when can-reassign?
       [inspection-reassign/selector tr authority-group-id false])
     (when (<sub [::process/signal-permitted? target [:process.signal/cancel-booking
                                                      {:booking-id (:db/id booking)}]])
       [render-cancel-toggle tr on-cancel])]))


(defn render-booking-modal
  "Shared modal for creating, editing and cancelling bookings (inspection requests)"
  [tr target]
  (when-let [open? (<sub [::dialog-open?])]
    (let [{:keys [editing? date-changeable?]
           :as   booking}                    (<sub [::booking-data])
          target-id                          (:db/id target)
          can-reassign?                      (and (du/in-state? :booking.state/dispatched booking)
                                                  (<sub [:session/dispatcher-user?])
                                                  (<sub [::ff/enabled? :feature-flag.id/reassign-from-modify-request-modal]))
          close                              #(>evt [::dialog-close])
          assigning-to                       (<sub [::inspection-reassign/assigning-to])
          assigning-ids                      (<sub [::inspection-reassign/assigning-ids])
          on-create                          #(>evt [::create-booking target-id])
          on-update                          (fn []
                                               (>evt [::update-booking target-id])
                                               (when (and can-reassign? assigning-to)
                                                 (>evt [::inspection-reassign/do-assign assigning-to assigning-ids])))
          on-cancel                          #(>evt [::cancel-booking target-id])
          title                              (cond
                                               editing?            (tr [:booking/edit-booking])
                                               (book-terminology?) (tr [:booking/book-inspection])
                                               :else               (tr [:booking/new-booking]))
          auth-group                         (-> target :entity/parent :domain/authority-group)]
      [ui-modal/composed-input-modal
       {:open       open?
        :class-name "modal-overflow-visible"
        :on-close   (constantly false)} ;; WIN-941, a fix for a bug with Carbon modal dialogs
       (cc/modal-header {:button-on-click close
                         :title           title})
       (when open?
         (cc/modal-body
           {}
           (if editing?
             [render-edit-booking-form target tr booking date-changeable? auth-group on-cancel can-reassign?]
             [render-new-booking-form tr target auth-group])))
       (cc/modal-footer
         {}
         (cc/button
           {:kind     :secondary
            :on-click close}
           (if editing?
             (tr [:close])
             (tr [:cancel])))
         (cc/button
           {:kind     :primary
            :disabled (not (<sub [::can-save? auth-group]))
            :on-click (if editing? on-update on-create)}
           (if editing?
             (tr [:booking/update-booking])
             (tr [:booking/create-booking]))))])))
